/* Keyboard accessibility for Froala's inline toolbar and its selection
 * popups (e.g. the link.edit popup with Open / Edit / Unlink that appears
 * when the caret is inside an existing link).
 *
 * Root cause: Froala renders toolbar and popup buttons with tabindex="-1",
 * which prevents real DOM focus. Its blur handler only keeps the toolbar
 * visible when a child of .fr-toolbar has :focus — with tabindex="-1" that
 * check always fails and the toolbar closes as soon as the editor blurs
 * (e.g. on Tab or ArrowDown). Popups have the same structural problem.
 *
 * Fix:
 *   1. Promote buttons to tabindex="0" while their container is visible
 *      so Froala's blur guard finds :focus within the container and keeps
 *      it open.
 *   2. A document-level CAPTURE-phase listener intercepts Tab when the
 *      editor has focus. Capture fires before every element-level handler
 *      (including Froala's own queue), so e.preventDefault reliably stops
 *      the default focus-move before Froala can react.
 *   3. A second document capture listener handles arrow navigation and
 *      Escape while a container button has focus.
 *
 * "Container" below means either the inline toolbar ($tb) or one of the
 * accessible popups listed in ACCESSIBLE_POPUPS. When multiple are
 * visible, popup wins — popups only appear for a specific selection
 * (link, image, ...) and are the user's active target.
 */

// Popups we extend keyboard accessibility to. Order defines priority
// when multiple are visible at once.
const ACCESSIBLE_POPUPS = ['link.edit'];

type FocusContainer =
  | { kind: 'toolbar'; el: HTMLElement }
  | { kind: 'popup'; name: string; el: HTMLElement };

/* @ngInject */
export default function setupToolbarKeyboardAccessibility(
  getFroalaInstance: () => any,
): void {
  const TOOLBAR_BTN_SELECTOR = 'a.fr-command, button.fr-command, div.fr-command';

  const getToolbarEl = (): HTMLElement | null => {
    const inst = getFroalaInstance();
    return (inst.$tb as any)?.get?.(0) ?? null;
  };

  const getPopupEl = (name: string): HTMLElement | null => {
    const inst = getFroalaInstance();
    const $p = inst.popups?.get?.(name);
    if (!$p) return null;
    return ($p.get?.(0) ?? $p[0]) ?? null;
  };

  const isPopupVisible = (name: string): boolean => {
    const inst = getFroalaInstance();
    return !!inst.popups?.isVisible?.(name);
  };

  const isElVisible = (el: HTMLElement | null): boolean => (
    !!el && window.getComputedStyle(el).display !== 'none'
  );

  const isInlineToolbarVisible = (): boolean => isElVisible(getToolbarEl());

  // Resolve a popup name to a FocusContainer only if the popup is both
  // marked visible AND has a rendered, still-connected DOM element.
  // Returning null when the element is missing avoids non-null
  // assertions and tolerates the popup being torn down between checks.
  // isConnected is a belt-and-suspenders guard — isElVisible uses
  // getComputedStyle, which can return values on detached nodes.
  const resolvePopupContainer = (name: string): FocusContainer | null => {
    if (!isPopupVisible(name)) return null;
    const el = getPopupEl(name);
    if (!el || !el.isConnected || !isElVisible(el)) return null;
    return { kind: 'popup', name, el };
  };

  const resolvePopupContainerContaining = (
    name: string,
    target: Element,
  ): FocusContainer | null => {
    if (!isPopupVisible(name)) return null;
    const el = getPopupEl(name);
    if (!el || !el.isConnected || !el.contains(target)) return null;
    return { kind: 'popup', name, el };
  };

  // Container we'd route Tab-from-editor into. Popups beat the toolbar:
  // popups are selection-triggered (link, image, ...) so they represent
  // the user's active target even when the toolbar is still visible.
  const getActiveContainer = (): FocusContainer | null => {
    const popup = ACCESSIBLE_POPUPS
      .map(resolvePopupContainer)
      .find((c): c is FocusContainer => c !== null);
    if (popup) return popup;
    const tb = getToolbarEl();
    if (!tb || !isElVisible(tb)) return null;
    return { kind: 'toolbar', el: tb };
  };

  // Container currently containing document.activeElement.
  const getContainerForFocus = (): FocusContainer | null => {
    const active = document.activeElement;
    if (!active) return null;
    const popup = ACCESSIBLE_POPUPS
      .map((name) => resolvePopupContainerContaining(name, active))
      .find((c): c is FocusContainer => c !== null);
    if (popup) return popup;
    const tb = getToolbarEl();
    if (tb?.contains(active)) return { kind: 'toolbar', el: tb };
    return null;
  };

  const isContainerVisible = (container: FocusContainer): boolean => (
    container.kind === 'popup'
      ? isPopupVisible(container.name)
      : isInlineToolbarVisible()
  );

  const getContainerButtons = (containerEl: HTMLElement): HTMLElement[] => (
    Array.from(
      containerEl.querySelectorAll(TOOLBAR_BTN_SELECTOR) as NodeListOf<HTMLElement>,
    ).filter((btn) => (
      btn.offsetParent !== null
      && !btn.classList.contains('fr-disabled')
      // Froala hides closed dropdown panels with aria-hidden="true" (not
      // display:none), so offsetParent is still non-null for their items.
      // Exclude any button that lives inside an aria-hidden ancestor.
      && !btn.closest('[aria-hidden="true"]')
    ))
  );

  const setContainerTabIndex = (containerEl: HTMLElement | null, tabIndexValue: string) => {
    if (!containerEl) return;
    containerEl
      .querySelectorAll(TOOLBAR_BTN_SELECTOR)
      .forEach((btn) => btn.setAttribute('tabindex', tabIndexValue));
  };

  // Returns the open dropdown menu element that contains the given element,
  // or null if the element is not inside an open dropdown panel.
  const getOpenDropdownMenu = (el: Element): HTMLElement | null => {
    const menu = el.closest('.fr-dropdown-menu') as HTMLElement | null;
    // Froala hides closed dropdown panels via aria-hidden="true" (not
    // display:none). Return null if the menu is inside a hidden ancestor
    // so callers can trust a non-null result means "visibly open".
    if (menu?.closest('[aria-hidden="true"]')) return null;
    return menu;
  };

  // Returns only the navigable items inside a specific dropdown menu.
  const getDropdownItems = (dropdownMenu: HTMLElement): HTMLElement[] => Array.from(
    dropdownMenu.querySelectorAll(TOOLBAR_BTN_SELECTOR) as NodeListOf<HTMLElement>,
  ).filter((btn) => (
    btn.offsetParent !== null
    && !btn.classList.contains('fr-disabled')
  ));

  // Returns the toggle button that owns a given dropdown menu.
  const getDropdownToggle = (dropdownMenu: HTMLElement): HTMLElement | null => {
    // Froala structure: .fr-command.fr-dropdown + .fr-dropdown-menu (siblings)
    const toggle = dropdownMenu.previousElementSibling;
    if (toggle && (toggle as HTMLElement).classList?.contains('fr-dropdown')) {
      return toggle as HTMLElement;
    }
    return null;
  };

  // Handles arrow / Tab / Enter / Space / Escape while focus is inside any
  // accessible container (inline toolbar or link.edit popup).
  const handleContainerKeydown = (e: KeyboardEvent) => {
    const container = getContainerForFocus();
    if (!container) return;

    const mouseOpts: MouseEventInit = { bubbles: true, cancelable: true, button: 0, view: window };

    // Check if focus is inside an open dropdown panel. If so, scope
    // arrow navigation to only that panel's items so focus cannot
    // escape and cause Froala to close the dropdown.
    const openDropdown = getOpenDropdownMenu(document.activeElement!);
    const buttons = openDropdown
      ? getDropdownItems(openDropdown)
      : getContainerButtons(container.el).filter((btn) => !btn.closest('.fr-dropdown-menu'));

    // Prefer an exact match; fall back to checking whether the focused
    // element is a child of a known button (e.g. a span/SVG inside the
    // font-style dropdown toggle <button>).
    let idx = buttons.indexOf(document.activeElement as HTMLElement);
    if (idx === -1) {
      idx = buttons.findIndex((btn) => btn.contains(document.activeElement));
    }
    if (idx === -1) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        e.stopImmediatePropagation();
        getFroalaInstance().events.disableBlur();
        buttons[(idx + 1) % buttons.length]?.focus();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        e.stopImmediatePropagation();
        getFroalaInstance().events.disableBlur();
        buttons[(idx - 1 + buttons.length) % buttons.length]?.focus();
        break;
      case 'Tab': {
        // WAI-ARIA toolbar pattern: Tab/Shift+Tab exits the container and
        // moves focus to the next/previous page element. Arrow keys handle
        // in-container navigation instead.
        // Do NOT preventDefault — let the browser's native Tab advance
        // focus from the editor. Stop propagation so Froala's own handlers
        // don't interfere with the focus move.
        e.stopImmediatePropagation();
        const froala = getFroalaInstance();
        froala.events.enableBlur();
        froala.selection.restore();
        // Return focus to the editor first so the browser's Tab/Shift+Tab
        // moves relative to the editor's position in the tab order, not
        // the container button's (container buttons are outside the
        // natural tab order).
        froala.el.focus();
        break;
      }
      case 'Enter':
      case ' ': {
        // Froala container items are <a>/<div> elements that don't receive
        // browser-native activation on Enter/Space — trigger it manually.
        //
        // Froala's bindClick binds TWO handlers:
        //   mousedown → marks button "fr-selected"
        //   mouseup   → if fr-dropdown: open/position dropdown panel
        //               else: call r() → commands.exec(cmd, params)
        //
        // Dropdown toggle buttons (fr-dropdown class) MUST go through the
        // full mousedown+mouseup sequence because the dropdown toggle logic
        // positions the panel using the button's DOM offset. Do NOT restore
        // the selection here — the fr-marker nodes must stay in the DOM so
        // they are available when the user later activates a dropdown item.
        //
        // Regular buttons restore the selection and call commands.exec()
        // directly (same as Froala's internal r()), then return focus to
        // the button if the container is still visible.
        e.preventDefault();
        e.stopImmediatePropagation();
        const activeBtn = document.activeElement as HTMLElement;
        const froala = getFroalaInstance();
        if (activeBtn.classList.contains('fr-dropdown')) {
          activeBtn.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
          activeBtn.dispatchEvent(new MouseEvent('mouseup', mouseOpts));
          // Do NOT call enableBlur() here: Froala's mouseup handler calls
          // disableBlur() internally when it opens the dropdown panel, so
          // calling enableBlur() synchronously after would undo that and cause
          // the toolbar to dismiss before the user can select a dropdown item.
          // The disableBlur/enableBlur pair is balanced via the toolbar.hide
          // event handler, which calls enableBlur whenever the toolbar hides.

          // After Froala opens the dropdown, move focus to the first item so
          // arrow keys navigate within the panel.
          const dropdownMenu = activeBtn.nextElementSibling as HTMLElement | null;
          if (dropdownMenu?.classList.contains('fr-dropdown-menu')) {
            const items = getDropdownItems(dropdownMenu);
            if (items.length) {
              froala.events.disableBlur();
              items[0].focus();
            }
          }
        } else {
          const cmd = activeBtn.getAttribute('data-cmd');
          if (cmd) {
            const params: string[] = [];
            for (let i = 1; activeBtn.getAttribute(`data-param${i}`) !== null; i += 1) {
              params.push(activeBtn.getAttribute(`data-param${i}`) as string);
            }

            // Capture the dropdown toggle BEFORE exec(), because the command
            // may trigger DOM mutations that detach the dropdown menu and its
            // contents, making openDropdown a stale reference afterwards.
            const dropdownToggle = openDropdown ? getDropdownToggle(openDropdown) : null;

            // Re-enable blur so the editor behaves normally during exec.
            froala.events.enableBlur();
            froala.el.focus();
            froala.selection.restore();
            froala.commands.exec(cmd, params);

            // Froala's bulkRefresh (~50 ms debounce) strips fr-active from
            // all buttons when the editor lacks focus. Delay refocusing
            // the container button so bulkRefresh runs while the editor
            // still has focus, preserving correct fr-active states.
            pendingRefocusTimer = setTimeout(() => {
              pendingRefocusTimer = null;
              // The command may have hidden the container (e.g. linkRemove
              // hides link.edit). Bail if so — focus already sits on the
              // editor from the enableBlur/el.focus() calls above.
              if (!activeBtn.isConnected || !isContainerVisible(container)) return;

              // Save selection for subsequent keyboard activations.
              froala.selection.save();

              if (dropdownToggle) {
                // Close dropdown, return focus to its toggle. Guard against
                // a stale/detached node from toolbar re-renders.
                if (dropdownToggle.isConnected) {
                  dropdownToggle.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
                  dropdownToggle.dispatchEvent(new MouseEvent('mouseup', mouseOpts));
                  froala.events.disableBlur();
                  dropdownToggle.focus();
                } else {
                  froala.events.disableBlur();
                  froala.el.focus();
                }
              } else {
                // Return focus to the activated button for continued
                // arrow-key navigation (guard against detached nodes).
                froala.events.disableBlur();
                if (activeBtn.isConnected) {
                  activeBtn.focus();
                } else {
                  froala.el.focus();
                }
              }
            }, 100);
          }
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        e.stopImmediatePropagation();
        const froala = getFroalaInstance();

        // If focus is inside an open dropdown, close just the dropdown and
        // return focus to its toggle button rather than exiting the container.
        if (openDropdown) {
          const toggle = getDropdownToggle(openDropdown);
          if (toggle) {
            // Simulate a click on the toggle to close the dropdown via
            // Froala's own toggle logic, then restore focus to it.
            toggle.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
            toggle.dispatchEvent(new MouseEvent('mouseup', mouseOpts));
            froala.events.disableBlur();
            toggle.focus();
            break;
          }
        }

        froala.events.enableBlur();
        froala.selection.restore();
        froala.el.focus();
        // Froala may re-show the container immediately after el.focus() because
        // the selection is still active. Hide it explicitly so a single Escape
        // closes it rather than requiring a second press.
        if (container.kind === 'popup') {
          froala.popups.hide(container.name);
        } else {
          froala.toolbar.hide();
        }
        break;
      }
      default:
        break;
    }
  };

  // Intercepts Tab from the editor and routes focus into the active
  // container (popup first, inline toolbar otherwise). Runs in capture
  // phase so it fires before Froala's own handlers. Only Tab is used to
  // enter the container, following the standard WAI-ARIA toolbar pattern.
  const handleEditorTabIntoContainer = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const froala = getFroalaInstance();
    if (!froala.el.contains(document.activeElement)) return;

    const container = getActiveContainer();
    if (!container) return;

    const buttons = getContainerButtons(container.el);
    if (!buttons.length) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    setContainerTabIndex(container.el, '0');
    froala.events.disableBlur();
    froala.selection.save();
    buttons[0].focus();
  };

  let pendingRefocusTimer: ReturnType<typeof setTimeout> | null = null;

  const instance = getFroalaInstance();

  if (instance.__keyboardAccessibilitySetup) return;
  instance.__keyboardAccessibilitySetup = true;

  // Inline toolbar show/hide: promote/demote tabindex on its buttons.
  instance.events.on('toolbar.show', () => {
    setContainerTabIndex(getToolbarEl(), '0');
  });
  instance.events.on('toolbar.hide', () => {
    setContainerTabIndex(getToolbarEl(), '-1');
    getFroalaInstance().events.enableBlur();
  });

  // Same treatment for each accessible popup (link.edit, ...). Froala
  // fires 'popups.show.<name>' / 'popups.hide.<name>' — same dotted form
  // already used by the colors plugin.
  ACCESSIBLE_POPUPS.forEach((name) => {
    instance.events.on(`popups.show.${name}`, () => {
      setContainerTabIndex(getPopupEl(name), '0');
    });
    instance.events.on(`popups.hide.${name}`, () => {
      setContainerTabIndex(getPopupEl(name), '-1');
      getFroalaInstance().events.enableBlur();
    });
  });

  // Keydown listeners live for the editor's entire lifetime. Both
  // handlers early-return when focus isn't in a known container, so
  // leaving them attached is cheap and avoids re-binding on every
  // toolbar/popup show/hide.
  document.addEventListener('keydown', handleEditorTabIntoContainer, true);
  document.addEventListener('keydown', handleContainerKeydown, true);

  instance.events.on('destroy', () => {
    if (pendingRefocusTimer !== null) {
      clearTimeout(pendingRefocusTimer);
      pendingRefocusTimer = null;
    }
    document.removeEventListener('keydown', handleEditorTabIntoContainer, true);
    document.removeEventListener('keydown', handleContainerKeydown, true);
  });
}
